"use client"

import { useState, useEffect } from "react"
import { connect } from "react-redux"
import { Plus, Search, RefreshCw, Settings, Upload, Filter, Box } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import "../../components/ui/skeleton";
import { useToast } from "../../components/ui/use-toast"
import InventoryTable from "./components/InventoryTable"
import InventoryModal from "./components/InventoryModal"
import InventoryFilterBar from "./components/InventoryFilterBar"
import ConfigurationModal from "./components/ConfigurationModal"
import { getInventoryItems } from "../../../ApiCalls/mantisFlowsInventory"
import "../../../ApiCalls/mantisFlowsCategories";
import { selectAuthState } from "../../../store/selectors/authSelectors"
import BulkUploadModal from "./components/BulkUploadModal"

function InventoryManagement({ token, mantisBusiness, user }) {
  const [inventoryItems, setInventoryItems] = useState([])
  const [customColumns, setCustomColumns] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const [categories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [modalMode, setModalMode] = useState("view")
  const [filters, setFilters] = useState({
    lowStock: false,
    category: null,
    subcategory: null
  })
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  
  const { toast } = useToast()

  // Cargar items al iniciar
  useEffect(() => {
    loadInventoryItems()
    
    // Establecer listener para evento de carga
    window.addEventListener('inventoryLoading', handleLoadingEvent)
    
    return () => {
      window.removeEventListener('inventoryLoading', handleLoadingEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryItems, searchTerm, filters])

  // Manejar evento de carga
  const handleLoadingEvent = (event) => {
    setLoading(event.detail)
  }

  // Cargar items de inventario
  const loadInventoryItems = async () => {
    try {
      setLoading(true)
      const response = await getInventoryItems(mantisBusiness._id)
      
      if (response.success) {
        setInventoryItems(response.data)
        setFilteredItems(response.data)
        setCustomColumns(response.customColumns || [])
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los elementos de inventario",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error("Error al cargar elementos de inventario:", error)
      toast({
        title: "Error",
        description: "Error al cargar los elementos de inventario",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  // Filtrar items
  const applyFilters = () => {
    let filtered = [...inventoryItems]
    
    // Aplicar búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(item => 
        (item.name && item.name.toLowerCase().includes(term)) || 
        (item.sku && item.sku.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term))
      )
    }
    
    // Aplicar filtro de bajo stock
    if (filters.lowStock) {
      filtered = filtered.filter(item => 
        item.current_stock <= item.min_stock_threshold
      )
    }
    
    // Aplicar filtro de categoría
    if (filters.category) {
      filtered = filtered.filter(item => 
        item.category_id === filters.category
      )
    }
    
    // Aplicar filtro de subcategoría
    if (filters.subcategory) {
      filtered = filtered.filter(item => 
        item.subcategory_id === filters.subcategory
      )
    }
    
    setFilteredItems(filtered)
  }

  // Abrir modal para crear un nuevo item
  const handleCreateItem = () => {
    setSelectedItem(null)
    setModalMode("create")
    setShowModal(true)
  }

  // Abrir modal para ver/editar un item
  const handleItemClick = (item) => {
    setSelectedItem(item)
    setModalMode("view")
    setShowModal(true)
  }

  // Manejar cambio en los filtros
  const handleFilterChange = (newFilters) => {
    setFilters({...filters, ...newFilters})
  }

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false)
    // Recargar items si se estaba en modo crear o editar
    if (modalMode !== "view") {
      loadInventoryItems()
    }
  }

  // Cambiar a modo edición
  const handleEditMode = () => {
    setModalMode("edit")
  }

  return (
    <div className="p-6 space-y-6">
      <div className="max-w-full">
        {/* Clean UI Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
            Gestión de Inventario
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            Gestiona y rastrea los elementos de tu inventario
          </p>
        </div>

        {/* Search bar y botones en la misma fila */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar inventario, categorías o subcategorías..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded border-slate-300 dark:border-slate-600"
            />
          </div>
          <div className="flex gap-3 w-full md:w-auto justify-end">
            <Button
              onClick={() => setShowBulkModal(true)}
              variant="outline"
              className="rounded border-slate-300 dark:border-slate-600"
            >
              <Upload className="h-4 w-4 mr-2" />
              Carga Masiva
            </Button>
            <Button
              onClick={handleCreateItem}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Elemento
            </Button>
            <Button
              onClick={loadInventoryItems}
              variant="outline"
              size="sm"
              className="rounded border-slate-300 dark:border-slate-600"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfigModal(true)}
              className="rounded border-slate-300 dark:border-slate-600"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filter Toggle - Mobile */}
        <div className="mb-4 sm:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="rounded border-slate-300 dark:border-slate-600"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
        </div>

        {/* Filters Bar - Desktop Always Visible, Mobile Collapsible */}
        <div className={`mb-6 ${showFilters || window.innerWidth >= 640 ? 'block' : 'hidden'}`}>
          <InventoryFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={filters}
            onFilterChange={handleFilterChange}
            onRefresh={loadInventoryItems}
            itemCount={filteredItems.length}
            totalCount={inventoryItems.length}
            categories={categories}
          />
        </div>

        {/* Clean UI Stats Cards */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded">
                  <Box className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Items</p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {inventoryItems.length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded">
                  <Search className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Filtrados</p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {filteredItems.length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded">
                  <Upload className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Poco Stock</p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {inventoryItems.filter(item => item.current_stock <= item.min_stock_threshold).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                  <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Categorías</p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {categories.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de inventario */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm overflow-hidden">
          <InventoryTable
            items={filteredItems}
            onItemClick={handleItemClick}
            categories={categories}
            isLoading={loading}
            clientId={mantisBusiness._id}
            searchTerm={searchTerm}
            customColumns={customColumns}
          />
        </div>
      </div>

      {/* Modales */}
      {showModal && (
        <InventoryModal
          item={selectedItem}
          mode={modalMode}
          onClose={handleCloseModal}
          onEditMode={handleEditMode}
          clientId={mantisBusiness._id}
          categories={categories}
          onSave={loadInventoryItems}
        />
      )}
      
      {showBulkModal && (
        <BulkUploadModal
          open={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          clientId={mantisBusiness._id}
          customColumns={customColumns}
          onSuccess={loadInventoryItems}
        />
      )}
      
      {showConfigModal && (
        <ConfigurationModal
          clientId={mantisBusiness._id}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </div>
  );
}

export default connect(selectAuthState)(InventoryManagement)
